import React, { useId, useState } from 'react';
import { IconChevronDown, IconTable } from '@tabler/icons-react';
import type { DropOff } from '../../../lib/campaign-analytics/funnel';
import { chartColor } from './chartTheme';
import { DataTable } from './charts/DataTable';

/** Props for {@link DropOffCard}. */
export interface DropOffCardProps {
  dropOff: DropOff;
}

/** Responses that stopped or ended early. */
function unfinishedCount(dropOff: DropOff): number {
  return dropOff.total - dropOff.completed - dropOff.unknown;
}

/**
 * Rounded columns over schema position: how many respondents reached each
 * question, with stop counts printed where unfinished conversations ended.
 */
export const DropOffCard: React.FC<DropOffCardProps> = ({ dropOff }) => {
  const [table, setTable] = useState(false);
  const max = Math.max(1, ...dropOff.steps.map(step => step.reached));
  /** Position and label of a step (1-based for people). */
  const name = (step: DropOff['steps'][number]) => `${step.position + 1} · ${step.label || 'Information'}`;
  /** Reach and stops at a step. */
  const counts = (step: DropOff['steps'][number]) =>
    [`${step.reached} reached`, step.stopped ? `${step.stopped} stopped` : '', step.ended ? `${step.ended} ended` : ''].filter(Boolean);
  return (
    <section data-testid="drop-off" className="tone-signal flex flex-col gap-4 rounded-[20px] p-6">
      <div className="flex justify-end">
        <button type="button" aria-label="Table" aria-pressed={table} onClick={() => setTable(value => !value)}
          className={`rounded-full p-2 hover:bg-[var(--chart-track)] focus-visible:outline-2 focus-visible:outline-ring ${table ? 'text-foreground' : 'text-subtle-foreground'}`}>
          <IconTable aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
      {table ? (
        <DataTable rowHeader="Question" columns={['Reached', 'Stopped', 'Ended']}
          rows={dropOff.steps.map(step => ({ key: step.fieldId, label: name(step), cells: [step.reached, step.stopped, step.ended] }))} />
      ) : (
        <div data-chart="drop-off" className="flex h-32 items-end gap-1">
          {dropOff.steps.map(step => {
            const stops = step.stopped + step.ended;
            return (
              <div key={step.fieldId} role="img" tabIndex={0} aria-label={`${name(step)}: ${counts(step).join(', ')}`} data-tip={name(step)} data-tip-detail={counts(step).join(' · ')}
                className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1 rounded-md focus-visible:outline-2 focus-visible:outline-ring">
                {stops > 0 && <span className="text-[11px] font-medium tabular-nums text-foreground">{stops}</span>}
                <span className="flex w-full flex-1 items-end overflow-hidden rounded-full" style={{ background: chartColor.track }}>
                  <span data-mark className="block w-full rounded-full" style={{ height: `${(step.reached / max) * 100}%`, background: chartColor.mark }} />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

/** A collapsed "Where people stopped" row, shown only when someone stopped early. */
export const DropOffRow: React.FC<DropOffCardProps> = ({ dropOff }) => {
  const id = useId();
  const [open, setOpen] = useState(false);
  const unfinished = unfinishedCount(dropOff);
  if (!dropOff.steps.some(step => step.stopped || step.ended)) return null;
  return (
    <div className="flex flex-col gap-3">
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen(value => !value)}
        className="flex w-full items-center justify-between gap-4 rounded-2xl border border-border/50 bg-surface px-6 py-4 text-left transition-colors hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-ring light:bg-surface-raised light:hover:bg-surface-raised-hover">
        <span className="text-sm font-medium text-foreground">Where people stopped</span>
        <span className="flex shrink-0 items-center gap-2 text-sm tabular-nums text-muted-foreground">
          {`${unfinished} of ${dropOff.total}`}
          <IconChevronDown aria-hidden="true" className={`h-4 w-4 transition-transform motion-reduce:transition-none ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && <div id={id}><DropOffCard dropOff={dropOff} /></div>}
    </div>
  );
};
